import React from 'react';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, Filler } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import './style.css';

const ProjectCardPayoff = ({ project }) => {

    const SECTION_HEADER = "Project Payoff";

    const financials = project.project_payoff_by_scenario[0]

    // Calculate totals
    const capitalFreedTotal = financials.by_value_type.capital_freed.total;

    // Sum property value upside
    const propertyValueTotal = financials.by_value_type.property_value_upside.by_year.reduce(
        (sum, value) => sum + value, 0
    );

    // Calculate opex totals - only include non-zero values
    const opexCategories = Object.entries(financials.by_value_type.opex)
        .map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value: value.total
        }))
        .filter(category => category.value > 0);

    const opexTotal = opexCategories.reduce((sum, category) => sum + category.value, 0);

    const valueStreamConfig = {
        capital_freed: {
            label: 'Capital Freed',
            color: '#7c1158'
        },
        opex: {
            label: 'Operating Expenses',
            color: '#0d88e6'
        },
        property_value_upside: {
            label: 'Property Value Upside',
            color: '#87bc45'
        }
    };

    // Create the gradient effects in the render
    const createGradient = (ctx, color) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, adjustColor(color, 30));  // Lighter at top
        gradient.addColorStop(0.5, color);                 // Original color in middle
        gradient.addColorStop(1, adjustColor(color, -30)); // Darker at bottom
        return gradient;
    };

    // Helper function to darken/lighten colors
    const adjustColor = (color, amount) => {
        const clamp = (val) => Math.min(255, Math.max(0, val));

        // Convert hex to RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Adjust each channel
        const adjustR = clamp(r + amount);
        const adjustG = clamp(g + amount);
        const adjustB = clamp(b + amount);

        // Convert back to hex
        return `#${adjustR.toString(16).padStart(2, '0')}${adjustG.toString(16).padStart(2, '0')}${adjustB.toString(16).padStart(2, '0')}`;
    };

    // Create arrays for chart data, only including non-zero values
    const chartData = [
        ...(capitalFreedTotal > 0 ? [{
            label: valueStreamConfig.capital_freed.label,
            value: capitalFreedTotal,
            color: valueStreamConfig.capital_freed.color
        }] : []),
        ...(propertyValueTotal > 0 ? [{
            label: valueStreamConfig.property_value_upside.label,
            value: propertyValueTotal,
            color: valueStreamConfig.property_value_upside.color
        }] : []),
        ...(opexTotal > 0 ? [{
            label: valueStreamConfig.opex.label,
            value: opexTotal,
            color: valueStreamConfig.opex.color,
            breakdown: opexCategories
        }] : [])
    ];

    // Calculate total value from the filtered data
    const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

    const data = {
        labels: chartData.map(item => item.label),
        datasets: [{
            data: chartData.map(item => item.value),
            backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;

                if (!chartArea) {
                    return;
                }

                // Get the color for this segment
                const segmentColor = chartData[context.dataIndex].color;
                return createGradient(ctx, segmentColor);
            },
            borderWidth: 0,
            hoverOffset: 4
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: {
                        family: 'inherit'
                    },
                    padding: 20,
                    boxWidth: 15,
                    usePointStyle: true
                },
                maxWidth: '100%',
                align: 'center'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const dataPoint = chartData[context.dataIndex];
                        if (dataPoint.breakdown) {
                            const lines = [
                                `Total: $${dataPoint.value.toLocaleString()}`,
                                '────────────',
                                ...dataPoint.breakdown.map(item =>
                                    `${item.name}: $${item.value.toLocaleString()}`
                                )
                            ];
                            return lines;
                        }
                        return `$${context.raw.toLocaleString()}`;
                    }
                }
            },
            datalabels: {
                color: '#fff',
                font: {
                    weight: 'bold',
                    size: 16,
                    family: 'inherit'
                },
                formatter: (value, context) => {
                    const percentage = ((value / totalValue) * 100).toFixed(0);
                    return `${percentage}%`;
                }
            }
        },
        cutout: '60%'
    };

    const years = Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`);

    const lineChartData = {
        labels: years,
        datasets: [
            {
                label: valueStreamConfig.capital_freed.label,
                data: financials.roi_assessment.capital_freed.by_year[9] > 0 ? financials.roi_assessment.capital_freed.by_year : [],
                borderColor: valueStreamConfig.capital_freed.color,
                backgroundColor: valueStreamConfig.capital_freed.color,
                fill: 'origin',
                tension: 0.4,
                pointRadius: 0
            },
            {
                label: valueStreamConfig.opex.label,
                data: financials.roi_assessment.opex.by_year[9] > 0 ?
                    financials.roi_assessment.opex.by_year.map((value, index) =>
                        value + financials.roi_assessment.capital_freed.by_year[index]
                    ) : [],
                borderColor: valueStreamConfig.opex.color,
                backgroundColor: valueStreamConfig.opex.color,
                fill: true,
                tension: 0.4,
                pointRadius: 0
            },
            {
                label: valueStreamConfig.property_value_upside.label,
                data: financials.roi_assessment.property_value_upside.by_year[9] > 0 ?
                    financials.roi_assessment.property_value_upside.by_year.map((value, index) =>
                        value + financials.roi_assessment.opex.by_year[index] + financials.roi_assessment.capital_freed.by_year[index]
                    ) : [],
                borderColor: valueStreamConfig.property_value_upside.color,
                backgroundColor: valueStreamConfig.property_value_upside.color,
                fill: true,
                tension: 0.4,
                pointRadius: 0
            }
        ].filter(dataset => dataset.data.length > 0)
    };

    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 2,
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (context) => `Year ${context[0].dataIndex + 1}`,
                    label: (context) => {
                        const index = context.dataIndex;
                        let value;

                        switch (context.dataset.label) {
                            case 'Capital Freed':
                                value = financials.roi_assessment.capital_freed.by_year[index];
                                break;
                            case 'Operating Expenses':
                                value = financials.roi_assessment.opex.by_year[index];
                                break;
                            case 'Property Value Upside':
                                value = financials.roi_assessment.property_value_upside.by_year[index];
                                break;
                        }

                        if (value > 0) {
                            return `${context.dataset.label}: ${value.toFixed(1)}x`;
                        }
                        return null;
                    }
                }
            },
            filler: {
                propagate: true
            },
            legend: {
                position: 'bottom',
                labels: {
                    font: {
                        family: 'sofia-pro, sans-serif'
                    },
                    padding: 20,
                    usePointStyle: true
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                stacked: false,
                beginAtZero: true,
                ticks: {
                    callback: value => `${value}x`
                },
                grid: {
                    color: '#E5E5E5'
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    return (
        <div className="project-card-section-container">
            <h2 className="project-card-section-header box-shadow-standard">{SECTION_HEADER}</h2>
            <div className="individual-project-cost-grid">
                <div className="individual-project-cost-column">
                    <h3 className="individual-project-cost-column-header box-shadow-standard">
                        Total Value Over 10 Years
                    </h3>

                    <div className="individual-project-financial-content">
                        <div className="individual-project-total-value box-shadow-standard">
                            ${totalValue.toLocaleString()}
                        </div>

                        <CurlyBraceDivider color="black" />

                        <div className="payoff-chart-container">
                            <Doughnut data={data} options={options} />
                        </div>
                    </div>
                </div>

                <div className="individual-project-cost-column">
                    <h3 className="individual-project-cost-column-header box-shadow-standard">
                        ROI Over Time
                    </h3>

                    <div className="roi-chart-container">
                        <Line data={lineChartData} options={lineChartOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectCardPayoff;